// src/services/userManagementService.js
import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    getDoc
} from 'firebase/firestore';
import { db } from './firebase';
import { ADMIN_DEFAULT_PERMISSIONS, EDITOR_DEFAULT_PERMISSIONS, USER_ROLES } from '../types/permissions';

/**
 * アカウントのユーザー一覧を取得
 */
export const getAccountUsers = async (accountId) => {
    try {
        const usersRef = collection(db, 'accounts', accountId, 'users');
        const snapshot = await getDocs(usersRef);

        return snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('ユーザー一覧の取得に失敗:', error);
        throw error;
    }
};

/**
 * ユーザーを招待（招待情報をFirestoreに保存）
 */
export const inviteUser = async (accountId, invitationData) => {
    try {
        const invitationsRef = collection(db, 'accounts', accountId, 'invitations');

        const invitation = {
            email: invitationData.email,
            role: invitationData.role,
            permissions: invitationData.permissions,
            invitedBy: invitationData.invitedBy,
            invitedAt: new Date(),
            status: 'pending', // pending, accepted, expired
            expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7日後
        };

        const docRef = await addDoc(invitationsRef, invitation);

        // メール送信機能
        try {
            // 開発環境では模擬メール機能を使用
            if (import.meta.env.DEV) {
                // 開発環境：招待URLを生成してコンソールに表示
                const baseUrl = window.location.origin;
                const invitationUrl = `${baseUrl}/invitation/${accountId}/${docRef.id}`;

                console.log('📧 開発環境: メール招待情報');
                console.log('🔗 招待URL:', invitationUrl);
                console.log('🏢 アカウント:', invitationData.accountName || 'Suisui アカウント');
                console.log('👥 ロール:', invitation.role === 'admin' ? '管理ユーザー' : '編集ユーザー');

                // 開発環境でのメール内容をコンソールに表示
                const emailText = `
=== 招待メール内容（サンプル） ===
件名: Suisui アカウントへの招待

[招待者名] さんから、${invitationData.accountName || 'Suisui アカウント'} への参加招待が届きました。

招待詳細:
- アカウント名: ${invitationData.accountName || 'Suisui アカウント'}
- あなたのロール: ${invitation.role === 'admin' ? '管理ユーザー' : '編集ユーザー'}

以下のURLをクリックして招待を受け入れてください：
${invitationUrl}

この招待は7日間有効です。
================================
                `;
                console.log(emailText);

            } else {
                // 本番環境：実際にAPIを呼び出し
                const response = await fetch('/api/send-invitation-email', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        toEmail: invitation.email,
                        inviterName: invitationData.inviterDisplayName || 'Suisui ユーザー',
                        accountName: invitationData.accountName || 'Suisui アカウント',
                        invitationId: docRef.id,
                        accountId: accountId,
                        role: invitation.role,
                        permissions: invitation.permissions
                    })
                });

                if (!response.ok) {
                    console.error('メール送信に失敗しました:', response.statusText);
                } else {
                    console.log('メール送信成功');
                }
            }
        } catch (emailError) {
            console.error('メール送信エラー:', emailError);
            // メール送信エラーは無視して続行
        }

        return {
            id: docRef.id,
            ...invitation
        };
    } catch (error) {
        console.error('ユーザー招待に失敗:', error);
        throw error;
    }
};

/**
 * 招待を受けてユーザーをアカウントに追加
 */
export const acceptInvitation = async (accountId, invitationId, userInfo) => {
    try {

        // 招待情報を取得
        const invitationRef = doc(db, 'accounts', accountId, 'invitations', invitationId);

        const invitationDoc = await getDoc(invitationRef);

        if (!invitationDoc.exists()) {
            throw new Error('招待が見つかりません');
        }

        const invitation = invitationDoc.data();

        if (invitation.status !== 'pending') {
            throw new Error('この招待は既に処理済みです');
        }

        if (invitation.expiresAt.toDate() < new Date()) {
            throw new Error('招待の有効期限が切れています');
        }

        // ユーザーをアカウントに追加
        console.log('👤 ユーザーをアカウントに追加中...', userInfo);

        const usersRef = collection(db, 'accounts', accountId, 'users');
        const newUser = {
            uid: userInfo.uid,
            email: userInfo.email,
            displayName: userInfo.displayName,
            role: invitation.role,
            permissions: invitation.permissions,
            joinedAt: new Date(),
            isActive: true
        };

        console.log('📝 新しいユーザーデータ:', newUser);
        await addDoc(usersRef, newUser);
        console.log('✅ ユーザー追加成功');

        // 招待のステータスを更新
        console.log('🔄 招待ステータスを更新中...');
        await updateDoc(invitationRef, {
            status: 'accepted',
            acceptedAt: new Date()
        });
        console.log('✅ 招待ステータス更新成功');

        return newUser;
    } catch (error) {
        console.error('❌ 招待の受け入れに失敗:', error);
        console.error('エラー詳細:', {
            code: error.code,
            message: error.message,
            stack: error.stack
        });
        throw error;
    }
};

/**
 * ユーザーの権限を更新
 */
export const updateUserPermissions = async (accountId, userId, permissions) => {
    try {
        const userRef = doc(db, 'accounts', accountId, 'users', userId);
        await updateDoc(userRef, {
            permissions: permissions,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error('権限の更新に失敗:', error);
        throw error;
    }
};

/**
 * ユーザーをアカウントから削除
 */
export const removeUser = async (accountId, userId) => {
    try {
        const userRef = doc(db, 'accounts', accountId, 'users', userId);
        await deleteDoc(userRef);
    } catch (error) {
        console.error('ユーザーの削除に失敗:', error);
        throw error;
    }
};

/**
 * 特定ユーザーの権限を取得
 */
export const getUserPermissions = async (accountId, userUid) => {
    try {
        const usersRef = collection(db, 'accounts', accountId, 'users');
        const q = query(usersRef, where('uid', '==', userUid));
        const snapshot = await getDocs(q);

        if (snapshot.empty) {
            return null;
        }

        const userData = snapshot.docs[0].data();
        return userData.permissions || [];
    } catch (error) {
        console.error('ユーザー権限の取得に失敗:', error);
        throw error;
    }
};

/**
 * ユーザーが特定の権限を持っているかチェック
 */
export const hasPermission = (userPermissions, permission) => {
    return userPermissions && userPermissions.includes(permission);
};

/**
 * アカウントオーナー（最初の管理ユーザー）かどうかチェック
 */
export const isAccountOwner = async (accountId, userUid) => {
    try {
        // アカウント情報から作成者をチェック
        const accountRef = doc(db, 'accounts', accountId);
        const accountDoc = await getDoc(accountRef);

        if (!accountDoc.exists()) {
            return false;
        }

        const accountData = accountDoc.data();
        return accountData.createdBy === userUid;
    } catch (error) {
        console.error('アカウントオーナーチェックに失敗:', error);
        return false;
    }
};
